/**
 * Storefront Cart
 *
 * Per-brand cart held in localStorage so it survives page reloads.
 * Storage key: `tpo_cart_<brand_id>`.
 *
 * Each line item is captured with a snapshot of product / variant info
 * at the moment of "add to cart" so the cart still renders meaningful
 * data even if the customer is offline at checkout (or the brand owner
 * happens to be editing the product right then).
 */

const KEY = (brandId) => `tpo_cart_${brandId}`;
const storage = typeof window !== 'undefined' ? window.localStorage : null;

const safeParse = (raw) => {
  try { return JSON.parse(raw); } catch (_) { return null; }
};

/**
 * A number, or the fallback.
 *
 * Used on everything read back out of storage. localStorage is a public
 * place — another script, an older version of the site, or a half-written
 * value can leave anything in it — and `Number('lots')` is NaN, which
 * propagates silently through a multiplication and ends up as the word "NaN"
 * on a checkout button. That is the most alarming thing a shop can show
 * somebody, and it comes from one unguarded coercion.
 */
const finite = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const NOOP_LISTENERS = new Set();

export function loadCart(brandId) {
  if (!storage || !brandId) return [];
  const raw = storage.getItem(KEY(brandId));
  const data = raw ? safeParse(raw) : null;
  return Array.isArray(data) ? data : [];
}

function persist(brandId, items) {
  if (!storage || !brandId) return;
  try {
    storage.setItem(KEY(brandId), JSON.stringify(items));
    NOOP_LISTENERS.forEach((fn) => { try { fn({ brandId, items }); } catch (_) {} });
  } catch (_) { /* quota / private mode */ }
}

/**
 * Swap the whole cart for another.
 *
 * Used when a signed-in customer's stored basket arrives from the server —
 * on a fresh device, or merged at sign-in. It goes through `persist`, so
 * anything watching the cart hears about it exactly as it would any other
 * change.
 */
export function replaceCart(brandId, items) {
  if (!brandId) return [];
  const next = Array.isArray(items) ? items : [];
  persist(brandId, next);
  return next;
}

export function clearCart(brandId) {
  if (!storage || !brandId) return;
  try { storage.removeItem(KEY(brandId)); } catch (_) {}
  NOOP_LISTENERS.forEach((fn) => { try { fn({ brandId, items: [] }); } catch (_) {} });
}

/**
 * Add a line item to the cart. If the same (variant_id) is already there,
 * the quantity is added on top.
 *
 *   addToCart(brandId, {
 *     product_id, variant_id, sku, name, variant_name, unit_price, currency, image_url, quantity
 *   })
 */
export function addToCart(brandId, item) {
  if (!brandId || !item?.variant_id) return loadCart(brandId);
  const items = loadCart(brandId);
  const existing = items.find((x) => String(x.variant_id) === String(item.variant_id));
  if (existing) {
    existing.quantity = Math.max(1, finite(existing.quantity, 1) + finite(item.quantity, 1));
  } else {
    items.push({
      product_id: item.product_id,
      variant_id: item.variant_id,
      sku: item.sku || '',
      name: item.name || '',
      variant_name: item.variant_name || '',
      unit_price: finite(item.unit_price),
      currency: item.currency || 'USD',
      image_url: item.image_url || '',
      quantity: Math.max(1, finite(item.quantity, 1)),
    });
  }
  persist(brandId, items);
  return items;
}

export function setQuantity(brandId, variant_id, quantity) {
  const items = loadCart(brandId);
  const next = items.flatMap((it) => {
    if (String(it.variant_id) !== String(variant_id)) return [it];
    const n = Number(quantity);
    // Not a number at all — a cleared or pasted-into quantity box. Leave the
    // line as it was: a mistyped character should not delete someone's item,
    // and it certainly should not store NaN.
    if (!Number.isFinite(n)) return [it];
    const q = Math.max(0, Math.floor(n));
    if (q === 0) return [];
    return [{ ...it, quantity: q }];
  });
  persist(brandId, next);
  return next;
}

export function removeFromCart(brandId, variant_id) {
  const items = loadCart(brandId).filter((it) => String(it.variant_id) !== String(variant_id));
  persist(brandId, items);
  return items;
}

export function cartTotals(items) {
  const list = Array.isArray(items) ? items : [];
  const subtotal = list.reduce((a, it) => a + finite(it?.unit_price) * finite(it?.quantity), 0);
  const count = list.reduce((a, it) => a + finite(it?.quantity), 0);
  const currency = list[0]?.currency || 'USD';
  return { subtotal: Math.round(subtotal * 100) / 100, count, currency };
}

export function subscribe(fn) {
  if (typeof fn !== 'function') return () => {};
  NOOP_LISTENERS.add(fn);
  return () => NOOP_LISTENERS.delete(fn);
}
